import React, { useState, useCallback, useRef } from 'react'
import { Input, Label, Button, Card, CardBody, Col, Row, FormGroup, Modal, ModalBody } from 'reactstrap';
import "../../assets/css/--component-agent.css"
import { createPrompt, getPromptTemplate, getSinglePromptTemplate, postCommpanyInfo, tidyPrompt } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { useNavigate } from 'react-router-dom'
import { postCompanyInfo } from '../../helper/api-methods';
import { useAppContext } from '../../context/AppContext';
import "../../assets/css/--component-prompt.css";
import templateImage from "../../assets/images/choose-template.png"
import ownImage from "../../assets/images/create-own.png"
import sampleTemplate from "../../assets/images/sample-template.png"
import Image from '../../components/Image';
import { getDefaultDataCollection } from '../../utils/defaultDataCollection';
import RubricPanel from '../../components/Prompt/RubricPanel';
import PromptSections, { assemblePrompt, emptySections, looksStructured, parseSections } from '../../components/Prompt/PromptSections';
import SixQuestions from '../../components/Prompt/SixQuestions';

const normalizePromptDynamicVariables = (prompt = "") => {
    return prompt.replace(/{{\s*([^{}]+?)\s*}}|{\s*([^{}]+?)\s*}/g, (match, doubleBraceKey, singleBraceKey) => {
        const key = (doubleBraceKey || singleBraceKey || "")
            .trim()
            .replace(/\s+/g, "_");

        return match.startsWith("{{") ? `{{${key}}}` : `{${key}}`;
    });
};

const CreateNewPrompt = () => {
    const history = useNavigate();
    const { markTaskDoneTour, activeTour } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [websiteAddress, setWebsiteAddress] = useState("");
    const [promptTemplate, setPromptTemplate] = useState([]);
    const [webModal, setWebModal] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [currentStep, setCurrentStep] = useState(1);
    const [type, setType] = useState("inbound");
    const [selection, setSelection] = useState("");
    const [choice, setChoice] = useState("");
    const [newPrompt, setNewPrompt] = useState({ title: "", firstMessage: "", purpose: "", prompt: "", customDataCollection: [] });
    const [errors, setErrors] = useState({ title: "", prompt: "", firstMessage: "", purpose: "", customDataCollection: [] });
    const purposeFieldRef = useRef(null);
    // The prompt is written as six boxes and assembled from them on save, so
    // the editor cannot produce an unstructured prompt in the first place.
    const [sections, setSections] = useState(emptySections());
    const [wizardOpen, setWizardOpen] = useState(false);

    /**
     * A prompt that arrives already written — from a template, or from the
     * website reader — goes into the boxes rather than past them. Sorting it
     * costs a model call, so it only runs when there is something to sort.
     */
    const loadIntoSections = useCallback(async (text) => {
        if (!text?.trim()) return;
        // Already sectioned — split it here. Only prose needs the model.
        if (looksStructured(text)) {
            const parsed = parseSections(text);
            setSections((current) => ({ ...current, ...parsed }));
            return;
        }
        try {
            const response = await tidyPrompt(text);
            const data = response?.data?.data ?? response?.data;
            if (response?.status && data?.sections) {
                setSections((current) => ({ ...current, ...data.sections }));
                setNewPrompt((current) => ({ ...current, prompt: assemblePrompt({ ...emptySections(), ...data.sections }) }));
            }
        } catch {
            // Leave the boxes empty; the customer still has the text on screen.
        }
    }, []);

    const scrollToPurposeField = useCallback(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
    
            document.getElementById('purpose')?.focus({ preventScroll: true });
        }, 50);
    }, []);    

    const fetchPromptTemplates = useCallback(async (type) => {
        setLoading(true);
        try {
            const data = await getPromptTemplate({ type: type });
            console.log("temp", data)
            if (data?.status) {
                setPromptTemplate(data.data || []);
                return true;
            } else {
                showToast("error", "Request Failed", data?.message || "Failed to fetch templates");
                return false;
            }
        } catch (err) {
            showToast("error", "Request Failed", err.message || "Network error occurred");
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        if (!newPrompt.title?.trim() || newPrompt.title.replace(/\s/g, '').length < 1) {
            newErrors.title = "Please provide valid title";
            isValid = false;
        }
        if (!newPrompt.firstMessage?.trim() || newPrompt.firstMessage.replace(/\s/g, '').length < 1) {
            newErrors.firstMessage = "Please provide first message";
            isValid = false;
        }
        if (!newPrompt.purpose?.trim() || newPrompt.purpose.replace(/\s/g, '').length < 1) {
            newErrors.purpose = "Please provide valid purpose";
            isValid = false;
        }
        if (!newPrompt.prompt?.trim() || newPrompt.prompt.replace(/\s/g, '').length < 1) {
            newErrors.prompt = "Please provide a valid prompt";
            isValid = false;
        }

        const customDataErrors = [];
        const customKeys = new Set(
            getDefaultDataCollection().map(field => field.key.toLowerCase())
        );

        newPrompt.customDataCollection?.forEach((field, index) => {
            const fieldErrors = {};
            const key = field.key?.trim();

            if (!key) {
                fieldErrors.key = "Please provide a key";
                isValid = false;
            } else if (!/^[A-Za-z0-9_]+$/.test(key)) {
                fieldErrors.key = "Only letters, numbers, and underscores are allowed";
                isValid = false;
            } else if (customKeys.has(key.toLowerCase())) {
                fieldErrors.key = "This key is already added";
                isValid = false;
            } else {
                customKeys.add(key.toLowerCase());
            }

            if (!field.type) {
                fieldErrors.type = "Please select a type";
                isValid = false;
            }

            if (!field.description?.trim() || field.description.replace(/\s/g, '').length < 1) {
                fieldErrors.description = "Please provide a description";
                isValid = false;
            }

            if (Object.keys(fieldErrors).length) {
                customDataErrors[index] = fieldErrors;
            }
        });

        if (customDataErrors.length) {
            newErrors.customDataCollection = customDataErrors;
        }

        setErrors(newErrors);
        if (newErrors.purpose) {
            scrollToPurposeField();
        }        
        return isValid;
    }, [newPrompt, scrollToPurposeField]);

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const normalizedPrompt = normalizePromptDynamicVariables(newPrompt.prompt);
            const normalizedFirstMessage = normalizePromptDynamicVariables(newPrompt.firstMessage);
            const formData = {
                title: newPrompt.title,
                firstMessage: normalizedFirstMessage,
                prompt: normalizedPrompt,
                domain: websiteAddress,
                company: companyName,
                purpose: newPrompt.purpose,
                customDataCollection: newPrompt.customDataCollection.map(field => ({
                    key: field.key.trim(),
                    type: field.type,
                    description: field.description.trim()
                }))
            };

            await createPrompt(formData);
            showToast("success", "Prompt Created", "Your new prompt has been created successfully.");
            const isInPromptTour = activeTour && activeTour.taskId === 1;

            if (isInPromptTour) {
                // Close any open driver.js instance first
                const driverInstance = document.querySelector('.driver-popover');
                if (driverInstance) {
                    // Find and trigger the close button
                    const closeBtn = driverInstance.querySelector('.driver-popover-close-btn');
                    if (closeBtn) {
                        closeBtn.click();
                    }
                }

                // Small delay to ensure driver.js is closed
                setTimeout(async () => {
                    await markTaskDoneTour(1);
                }, 100);                
            } else {
                await markTaskDoneTour(1, false);
                history('/prompts');
            }
        } catch (error) {
            showToast("error", "Prompt Request Failed", "Your new prompt could not be created.");
        } finally {
            setLoading(false);
        }
    };

    const websiteAddressHandler = async () => {
        setLoading(true);
        try {
            const data = await postCommpanyInfo({
                domain: websiteAddress,
                company: companyName,
                type: type
            });
            const written = data?.data?.result?.replace(/\*\*|###|---/g, '') || "";
            setNewPrompt(prev => ({ ...prev, prompt: written }));
            // The editor is sectioned, so a prompt that arrives already
            // written has to land in the boxes rather than behind them.
            await loadIntoSections(written);
            return true;
        } catch (error) {
            console.log("error", error);
            showToast("error", "Fetch Failed", "Unable to fetch data from website.");
            return false
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = useCallback((field, value) => {
        setNewPrompt(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    }, [errors]);

    const handleCustomDataChange = useCallback((index, field, value) => {
        const formattedValue = field === 'key'
            ? value.replace(/\s+/g, '_')
            : value;

        setNewPrompt(prev => ({
            ...prev,
            customDataCollection: prev.customDataCollection.map((item, itemIndex) => (
                itemIndex === index ? { ...item, [field]: formattedValue } : item
            ))
        }));

        if (errors.customDataCollection?.[index]?.[field]) {
            setErrors(prev => {
                const customDataCollection = [...(prev.customDataCollection || [])];

                customDataCollection[index] = {
                    ...customDataCollection[index],
                    [field]: ""
                };

                return { ...prev, customDataCollection };
            });
        }
    }, [errors.customDataCollection]);

    const handleAddCustomDataField = useCallback(() => {
        setNewPrompt(prev => ({
            ...prev,
            customDataCollection: [
                ...prev.customDataCollection,
                { key: "", type: "string", description: "" }
            ]
        }));
    }, []);

    const handleDeleteCustomDataField = useCallback((index) => {
        setNewPrompt(prev => ({
            ...prev,
            customDataCollection: prev.customDataCollection.filter((_, itemIndex) => itemIndex !== index)
        }));

        setErrors(prev => ({
            ...prev,
            customDataCollection: (prev.customDataCollection || []).filter((_, itemIndex) => itemIndex !== index)
        }));
    }, []);

    const handleFlowControl = async (action, data = null) => {
        // Reset errors for new action
        setErrors({});

        switch (action) {
            // STEP 1: User selects type and what they want to do
            case 'select_type_and_action':
                // Validate inputs
                if (!type) {
                    setErrors({ type: 'Please select a type' });
                    return;
                }

                if (!data?.selection) {
                    setErrors({ selection: 'Please choose an option' });
                    return;
                }

                setSelection(data.selection);

                // Scenario 1: Choose a Template
                if (data.selection === 'template') {
                    const resp = await fetchPromptTemplates(type)
                    if (!resp) return;
                    setCurrentStep(2); // Go to template selection screen
                }

                // Scenario 2: Create your Own
                else if (data.selection === 'own') {
                    // Stay on step 1 but show the additional choices
                    setCurrentStep(1);
                    refreshTourSpotlight();
                }
                break;

            // STEP 2: User selects website or manual (after "Create your Own")
            case 'select_creation_choice':
                if (!data?.choice) {
                    setErrors({ choice: 'Please select an option' });
                    return;
                }

                setChoice(data.choice);
                refreshTourSpotlight();

                // Scenario 3: Fetch from Website
                if (data.choice === 'website') {
                    setWebModal(true); // Show modal for URL input
                }

                // Scenario 4: Create Manually
                else if (data.choice === 'manual') {
                    setCurrentStep(3); // Go directly to prompt form
                }

                // Scenario 5: Answer six questions, and we write it
                else if (data.choice === 'questions') {
                    setCurrentStep(3);
                    setWizardOpen(true);
                }
                break;

            // STEP 3: Handle template selection (after choosing template)
            case 'select_template':
                if (!data?.templateId) {
                    setErrors({ template: 'Please select a template' });
                    return;
                }

                // find selected template
                const selectedTemplate = promptTemplate.find(t => t._id === data.templateId);
                console.log("selectedTemplate", selectedTemplate)
                if (selectedTemplate) {
                    // Pre-fill form with template data
                    setNewPrompt(prev => ({
                        ...prev,
                        prompt: selectedTemplate.prompt || '',
                        title: selectedTemplate.title ? selectedTemplate.title : selectedTemplate.industry,
                        purpose: selectedTemplate.purpose || '',
                        firstMessage: selectedTemplate.firstMessage || ''
                    }));
                    loadIntoSections(selectedTemplate.prompt || '');
                }

                setCurrentStep(3); // Go to prompt creation form
                break;

            // STEP 4: Handle website URL submission
            case 'fetch_from_website':
                if (!websiteAddress) {
                    setErrors({ websiteAddress: 'Please enter a website URL' });
                    return;
                }
                try {
                    // Make API call to fetch website data
                    const success = await websiteAddressHandler();
                    if (!success) {
                        return;
                    }
                    setWebModal(false);
                    setCurrentStep(3); // Go to prompt creation form
                } catch (error) {
                    showToast("error", "Fetch Failed", "Unable to fetch data from website.");
                } finally {
                    setLoading(false);
                }
                break;

            // Navigation handlers
            case 'back_to_selection':
                // Reset to initial selection screen
                setSelection('');
                setChoice('');
                setCurrentStep(1);
                break;

            case 'back_to_choices':
                // Go back to website/manual choices
                setChoice('');
                setCurrentStep(1);
                break;

            case 'close_website_modal':
                setWebModal(false);
                setWebsiteAddress('');
                break;

            // Final form submission
            case 'submit_prompt':
                // Validate all required fields
                handleSubmit();
                break;

            default:
                console.warn(`Unknown action: ${action}`);
        }
    };

    const refreshTourSpotlight = () => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 150);
    };
    

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid" id='prompt-form'>
                    <div className="py-3 row">
                        <div className="col-12">
                            <Row className="my-2 align-items-center">
                                <Col md={8}>
                                    <h4>Create New Prompt</h4>
                                    <p className='text-muted fs-12'>Create a new prompt</p>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row className="card-row">
                        <Col md={12}>
                            {/* STEP 1: Choose what you like to do? */}
                            {currentStep === 1 && (
                                <Card className='bg-white rounded-2'>
                                    <CardBody>
                                        <Row>
                                            <Col md={12}>
                                                <Label htmlFor="prompt-title" className="text-left text-black font-bold d-block fs-18">
                                                    Type
                                                </Label>
                                                <FormGroup check className='d-inline-block me-3'>
                                                    <Input
                                                        name="radio1"
                                                        type="radio"
                                                        checked={type === 'inbound'}
                                                        value="inbound"
                                                        onChange={(e) => { setType(e.target.value) }}
                                                    />
                                                    {' '}
                                                    <Label check className='text-black fw-500'>Inbound</Label>
                                                </FormGroup>
                                                <FormGroup check className='d-inline-block'>
                                                    <Input
                                                        name="radio1"
                                                        type="radio"
                                                        checked={type === 'outbound'}
                                                        value="outbound"
                                                        onChange={(e) => { setType(e.target.value) }}
                                                    />
                                                    {' '}
                                                    <Label check className='text-black fw-500'>Outbound</Label>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row className='mt-4'>
                                            <Col md={12}>
                                                <Label className="text-left text-black font-bold d-block fs-18">What would you like to do?</Label>
                                                <Row className="g-3">
                                                    <Col xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex">
                                                        <div
                                                            className={`option-box text-center w-100 d-flex flex-column justify-content-between p-3 ${selection === 'template' ? 'active' : ''}`}
                                                            onClick={() => handleFlowControl('select_type_and_action', { selection: 'template' })}
                                                            style={{ minHeight: '180px', cursor: 'pointer' }}
                                                        >
                                                            <div>
                                                                <img src={templateImage} className='img-fluid' alt="Template" />
                                                            </div>
                                                            <div className="mt-2">
                                                                <span className='fs-15 fw-500 text-black option-btn'>Choose a Template</span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex">
                                                        <div
                                                            className={`option-box text-center w-100 d-flex flex-column justify-content-between p-3 ${selection === 'own' ? 'active' : ''}`}
                                                            onClick={() => handleFlowControl('select_type_and_action', { selection: 'own' })}
                                                            style={{ minHeight: '180px', cursor: 'pointer' }}
                                                        >
                                                            <div>
                                                                <img src={ownImage} className='img-fluid' alt="Create your own" />
                                                            </div>
                                                            <div className="mt-2">
                                                                <span className='fs-15 fw-500 text-black option-btn'>Create your Own</span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>

                                        {/* If user selects: Create your own */}
                                        {selection === 'own' && (
                                            <Row className='mt-4'>
                                                <Col md={12}>
                                                    <Label className="text-left text-black font-bold d-block fs-18">Select your Choice:</Label>
                                                    <Row>
                                                        <Col xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex mb-2">
                                                            <div
                                                                className={`choice-box text-center w-100 ${choice === 'website' ? 'active' : ''}`}
                                                                onClick={() => handleFlowControl('select_creation_choice', { choice: 'website' })}
                                                            >
                                                                <span className='fs-15 text-black choice-btn'>Fetch from Website</span>
                                                            </div>
                                                        </Col>
                                                        <Col xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex mb-2">
                                                            <div
                                                                className={`choice-box text-center w-100 ${choice === 'manual' ? 'active' : ''}`}
                                                                onClick={() => handleFlowControl('select_creation_choice', { choice: 'manual' })}
                                                            >
                                                                <span className='fs-15 text-black choice-btn'>Create Manually</span>
                                                            </div>
                                                        </Col>
                                                        {/*
                                                          A fourth way in, not a replacement. Someone who
                                                          knows what they want keeps the other three.
                                                        */}
                                                        <Col xs={12} sm={6} md={4} lg={3} xl={2} className="d-flex mb-2">
                                                            <div
                                                                className={`choice-box text-center w-100 ${choice === 'questions' ? 'active' : ''}`}
                                                                onClick={() => handleFlowControl('select_creation_choice', { choice: 'questions' })}
                                                            >
                                                                <span className='fs-15 text-black choice-btn'>Answer Six Questions</span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )}

                                        {errors.selection && <p className="text-danger mt-2">{errors.selection}</p>}
                                        {errors.choice && <p className="text-danger mt-2">{errors.choice}</p>}

                                        {/* <Row className='mt-4'>
                                            <Col md={12} className='text-right'>
                                                {selection && (
                                                    <button
                                                        disabled={loading}
                                                        onClick={() => handleFlowControl('back_to_selection')}
                                                        className="px-4 py-2 rounded me-2 outline-gradient-btn"
                                                    >
                                                        <span>Back</span>
                                                    </button>
                                                )}
                                            </Col>
                                        </Row> */}
                                    </CardBody>
                                </Card>
                            )}

                            {/* STEP 2: If user selects: Choose a Template */}
                            {currentStep === 2 && (
                                <Card className='bg-white rounded-2'>
                                    <CardBody>
                                        {loading ? (
                                            <div className="text-center">Loading templates...</div>
                                        ) : (
                                            <>
                                                {/* Add d-flex flex-wrap to the Row and h-100 to template-card */}
                                                <Row className="d-flex flex-wrap">
                                                    {promptTemplate.length > 0 ? (
                                                        promptTemplate.map((template, i) => {
                                                            return (
                                                                <Col md={3} className='mb-3 d-flex' key={i}>
                                                                    <div className='template-card w-100 d-flex flex-column'>
                                                                        <Image src={template.image} alt="Template" className="img-fluid rounded-3" width='325px' height='178px' />
                                                                        <div className="flex-grow-1">
                                                                            <h5 className='fs-18 fw-400 text-left mt-3 ms-1'>{template.industry}</h5>
                                                                            <p className='fs-16 fw-400 ms-1'>{template.description}</p>
                                                                        </div>
                                                                        <Button
                                                                            onClick={() => handleFlowControl('select_template', { templateId: template._id })}
                                                                            className="font-normal py-2 px-3 rounded-3 fs-14 text-decoration-none text-white linear-gradient-btn w-100 mt-auto"
                                                                        >
                                                                            Start Using this Template
                                                                        </Button>
                                                                    </div>
                                                                </Col>
                                                            )
                                                        })
                                                    ) : (
                                                        <Col md={12}>
                                                            <div className="text-center">No templates available</div>
                                                        </Col>
                                                    )}
                                                </Row>
                                                <Row className='mt-4'>
                                                    <Col md={12} className='text-right'>
                                                        <button
                                                            disabled={loading}
                                                            onClick={() => handleFlowControl('back_to_selection')}
                                                            className="px-4 py-2 rounded me-2 outline-gradient-btn"
                                                        >
                                                            <span>Back</span>
                                                        </button>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            )}

                            {/* STEP 3: Prompt Creation Form (for manual, template, or website) */}
                            {currentStep === 3 && (
                                <Card className='bg-white rounded-2'>
                                    <CardBody>
                                        <Row className='mb-3'>
                                            <Col md={12} className='mb-4'>
                                                <Label htmlFor="prompt-title" className="text-left text-black font-bold">
                                                    Prompt Title
                                                </Label>
                                                <Input
                                                    id="prompt-title"
                                                    className='custom-input form-control rounded border-0 py-2'
                                                    type="text"
                                                    value={newPrompt.title}
                                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                                />
                                                {errors.title && <p className="invalid-feedback">{errors.title}</p>}
                                            </Col>

                                            <Col md={12} className='mb-4'>
                                                <Label htmlFor="firstmessage" className="text-left text-black font-bold">
                                                    First Message
                                                </Label>
                                                <Input
                                                    id="firstmessage"
                                                    className='custom-input form-control rounded border-0 py-2'
                                                    type="text"
                                                    value={newPrompt.firstMessage}
                                                    onChange={(e) => handleInputChange('firstMessage', e.target.value)}
                                                />
                                                {errors.firstMessage && <p className="invalid-feedback">{errors.firstMessage}</p>}
                                            </Col>

                                            <Col md={12} className='mb-4' ref={purposeFieldRef}>
                                                <Label htmlFor="purpose" className="text-left text-black font-bold">
                                                    Purpose
                                                </Label>
                                                <Input
                                                    id="purpose"
                                                    className='custom-input form-control rounded border-0 py-2'
                                                    type="text"
                                                    value={newPrompt.purpose}
                                                    onChange={(e) => handleInputChange('purpose', e.target.value)}
                                                />
                                                {errors.purpose && <p className="invalid-feedback">{errors.purpose}</p>}
                                            </Col>

                                            {/*
                                              The wizard is a modal over this form, not a replacement
                                              for it: closing it leaves the boxes exactly as they were,
                                              and finishing it fills them in.
                                            */}
                                            <SixQuestions
                                                open={wizardOpen}
                                                company={companyName}
                                                website={websiteAddress}
                                                type={type}
                                                onCancel={() => setWizardOpen(false)}
                                                onWritten={(written) => {
                                                    // Split here, not by the model: this text was
                                                    // assembled from our own headings a moment ago.
                                                    setSections(parseSections(written.prompt));
                                                    setNewPrompt((current) => ({
                                                        ...current,
                                                        prompt: written.prompt,
                                                        firstMessage: written.firstMessage || current.firstMessage,
                                                        title: current.title || written.title || '',
                                                        customDataCollection: [
                                                            ...current.customDataCollection,
                                                            ...(written.customDataCollection || []),
                                                        ],
                                                    }));
                                                    setWizardOpen(false);
                                                }}
                                            />
                                            <Col lg={8} md={12} className='mb-4'>
                                                <PromptSections
                                                    onOpenWizard={() => setWizardOpen(true)}
                                                    values={sections}
                                                    onChange={(next) => {
                                                        setSections(next);
                                                        handleInputChange('prompt', assemblePrompt(next));
                                                    }}
                                                />
                                                {errors.prompt && <p className="invalid-feedback d-block">{errors.prompt}</p>}
                                            </Col>
                                            {/*
                                              What the prompt is missing, beside the prompt rather than
                                              after a save. The check is free and instant, so there is no
                                              reason to make someone save a bad prompt to find out it is one.
                                            */}
                                            <Col lg={4} md={12} className='mb-4'>
                                                <RubricPanel
                                                    prompt={newPrompt.prompt}
                                                    firstMessage={newPrompt.firstMessage}
                                                />
                                            </Col>
                                            <Col md={12} className='mb-4'>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <Label className="text-left text-black font-bold mb-0">
                                                        Custom Data Collection
                                                    </Label>

                                                    <Button
                                                        type="button"
                                                        onClick={handleAddCustomDataField}
                                                        className="px-3 py-2 rounded linear-gradient-btn"
                                                    >
                                                        Add Properties
                                                    </Button>
                                                </div>
                                                {getDefaultDataCollection().map((field) => (
                                                    <Row className="align-items-end mb-3" key={field.key}>
                                                        <Col md={3} className="mb-2">
                                                            <Label className="text-left text-black font-bold">Key</Label>
                                                            <Input disabled className='custom-input form-control rounded border-0 py-2' value={field.key} />
                                                        </Col>

                                                        <Col md={3} className="mb-2">
                                                            <Label className="text-left text-black font-bold">Type</Label>
                                                            <Input disabled className='custom-input form-control rounded border-0 py-2' value={field.type} />
                                                        </Col>

                                                        <Col md={5} className="mb-2">
                                                            <Label className="text-left text-black font-bold">Description</Label>
                                                            <Input disabled className='custom-input form-control rounded border-0 py-2' value={field.description} />
                                                        </Col>

                                                        <Col md={1} className="mb-2">
                                                        </Col>
                                                    </Row>
                                                ))}

                                                {newPrompt.customDataCollection.map((field, index) => (
                                                    <Row className="align-items-end mb-3" key={index}>
                                                        <Col md={3} className="mb-2">
                                                            <Label className="text-left text-black font-bold">
                                                                Key
                                                            </Label>
                                                            <Input
                                                                className='custom-input form-control rounded border-0 py-2'
                                                                type="text"
                                                                placeholder="Key"
                                                                value={field.key}
                                                                onChange={(e) => handleCustomDataChange(index, 'key', e.target.value)}
                                                            />
                                                            {errors.customDataCollection?.[index]?.key && (
                                                                <p className="invalid-feedback">{errors.customDataCollection[index].key}</p>
                                                            )}
                                                        </Col>

                                                        <Col md={3} className="mb-2">
                                                            <Label className="text-left text-black font-bold">
                                                                Type
                                                            </Label>
                                                            <Input
                                                                className='custom-input form-control rounded border-0 py-2'
                                                                type="select"
                                                                value={field.type}
                                                                onChange={(e) => handleCustomDataChange(index, 'type', e.target.value)}
                                                            >
                                                                <option value="string">String</option>
                                                                <option value="boolean">Boolean</option>
                                                                <option value="integer">Integer</option>
                                                            </Input>
                                                            {errors.customDataCollection?.[index]?.type && (
                                                                <p className="invalid-feedback">{errors.customDataCollection[index].type}</p>
                                                            )}
                                                        </Col>

                                                        <Col md={5} className="mb-2">
                                                            <Label className="text-left text-black font-bold">
                                                                Description
                                                            </Label>
                                                            <Input
                                                                className='custom-input form-control rounded border-0 py-2'
                                                                type="text"
                                                                placeholder="Description"
                                                                value={field.description}
                                                                onChange={(e) => handleCustomDataChange(index, 'description', e.target.value)}
                                                            />
                                                            {errors.customDataCollection?.[index]?.description && (
                                                                <p className="invalid-feedback">{errors.customDataCollection[index].description}</p>
                                                            )}
                                                        </Col>

                                                        <Col md={1} className="mb-2">
                                                            <Button
                                                                type="button"
                                                                color="danger"
                                                                className="btn btn-sm rounded w-40 py-2"
                                                                onClick={() => handleDeleteCustomDataField(index)}
                                                                aria-label="Delete custom field"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                ))}
                                            </Col>

                                        </Row>

                                        <Row className='mt-4'>
                                            <Col md={12} className='text-right'>
                                                <button
                                                    onClick={() => {
                                                        if (selection === 'template') {
                                                            handleFlowControl('back_to_selection');
                                                        } else {
                                                            handleFlowControl('back_to_choices');
                                                        }
                                                    }}
                                                    className="px-4 py-2 rounded me-2 outline-gradient-btn"
                                                >
                                                    <span>Back</span>
                                                </button>
                                                <Button
                                                    disabled={loading}
                                                    onClick={() => handleFlowControl('submit_prompt')}
                                                    className="px-4 py-2 rounded linear-gradient-btn"
                                                >
                                                    {loading ? "Please Wait" : "Create Prompt"}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}
                        </Col>
                    </Row>

                    {/* Modal for website URL input */}
                    <Modal isOpen={webModal} toggle={() => handleFlowControl('close_website_modal')} centered={true} size='md' backdrop="static">
                        <ModalBody>
                            <Row>
                                <Col md={12} className=''>
                                    <div className="text-center">
                                        <h3 className="mt-2 mb-4 fs-26 text-dark fw-600">Fetch from Website</h3>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} className='mb-3'>
                                    <Label htmlFor="businessName" className="text-left text-black font-bold"> Website URL </Label>
                                    <input
                                        id="businessName"
                                        className='custom-input form-control rounded border-0 py-2'
                                        type="text"
                                        value={websiteAddress}
                                        onChange={(e) => { setWebsiteAddress(e.target.value) }}
                                    />
                                    {errors.websiteAddress && <p className="invalid-feedback">{errors.websiteAddress}</p>}
                                </Col>
                            </Row>
                            <Row className="text-center mt-3">
                                <Col>
                                    <button
                                        disabled={loading}
                                        onClick={() => handleFlowControl('close_website_modal')}
                                        className="px-2 py-2 rounded outline-gradient-btn w-100"
                                    >
                                        <span>Cancel</span>
                                    </button>
                                </Col>
                                <Col>
                                    <Button
                                        disabled={loading}
                                        onClick={() => handleFlowControl('fetch_from_website')}
                                        className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn w-100"
                                    >
                                        {loading ? "Fetching..." : "Fetch"}
                                    </Button>
                                </Col>
                            </Row>
                        </ModalBody>
                    </Modal>
                </div>
            </div>
        </div>
    );
};

export default CreateNewPrompt;